/**
 * FrameProfiler
 *
 * Her frame'de adım adım süreleri ölçer, aksi belirtilmedikçe 1 saniyede bir
 * özet tablo basar. 30ms'den uzun süren tek bir frame olduğunda anında breakdown
 * ile bir uyarı atar. Devre dışıyken (enabled=false) tüm ölçüm çağrıları no-op.
 *
 * Kullanım:
 *   const fp = new FrameProfiler();
 *   fp.setEnabled(true);
 *
 *   fp.beginFrame();
 *   fp.begin("animate-entities");
 *   // ...iş...
 *   fp.end("animate-entities");
 *   fp.endFrame();
 */

type BucketStats = {
  count: number;
  totalMs: number;
  maxMs: number;
  maxFrame: number;
};

export class FrameProfiler {
  private _enabled = false;
  private _flushEveryMs = 1000;
  private _slowFrameThresholdMs = 30;

  private _phaseStats = new Map<string, BucketStats>();
  private _entityStats = new Map<string, BucketStats>();
  private _gauges = new Map<string, { last: number; min: number; max: number }>();

  private _frameIndex = 0;
  private _frameStartMs = 0;
  private _frameEndMs = 0;
  private _frameTotalMs = 0;

  /** Bu frame içinde span başlangıçları. */
  private _phaseSpan = new Map<string, number>();
  /** Bu frame içinde adım süreleri (slow-frame breakdown için). */
  private _frameBreakdown = new Map<string, number>();

  private _lastFlushMs = 0;
  private _framesSinceFlush = 0;
  private _totalSinceFlushMs = 0;
  private _maxFrameSinceFlushMs = 0;

  setEnabled(v: boolean): void {
    this._enabled = v;
    if (v) {
      this._lastFlushMs = performance.now();
    } else {
      this._phaseStats.clear();
      this._entityStats.clear();
      this._phaseSpan.clear();
      this._frameBreakdown.clear();
      this._framesSinceFlush = 0;
      this._totalSinceFlushMs = 0;
      this._maxFrameSinceFlushMs = 0;
    }
  }

  get enabled(): boolean {
    return this._enabled;
  }

  setFlushInterval(ms: number): void {
    this._flushEveryMs = Math.max(100, ms);
  }

  setSlowFrameThreshold(ms: number): void {
    this._slowFrameThresholdMs = Math.max(0, ms);
  }

  beginFrame(): void {
    if (!this._enabled) return;
    const now = performance.now();
    this._frameIndex++;
    this._frameStartMs = now;
    this._phaseSpan.clear();
    this._frameBreakdown.clear();
    if (this._frameEndMs > 0) {
      // endFrame -> next beginFrame gap: browser vsync + diğer rAF callback'leri.
      // Normalde 60fps için ~16.67ms. Büyürse başka rAF iş yapıyor.
      const gap = now - this._frameEndMs;
      this._trackBucket(this._phaseStats, "__raf_gap__", gap);
      this._frameBreakdown.set("__raf_gap__", gap);
    }
  }

  endFrame(): void {
    if (!this._enabled) return;
    const now = performance.now();
    this._frameEndMs = now;
    this._frameTotalMs = now - this._frameStartMs;
    this._framesSinceFlush++;
    this._totalSinceFlushMs += this._frameTotalMs;
    if (this._frameTotalMs > this._maxFrameSinceFlushMs) {
      this._maxFrameSinceFlushMs = this._frameTotalMs;
    }

    this._trackBucket(this._phaseStats, "__total__", this._frameTotalMs);

    if (this._frameTotalMs >= this._slowFrameThresholdMs) {
      this._logSlowFrame();
    }

    if (now - this._lastFlushMs >= this._flushEveryMs) {
      this._flush(now);
    }
  }

  begin(label: string): void {
    if (!this._enabled) return;
    this._phaseSpan.set(label, performance.now());
  }

  end(label: string): void {
    if (!this._enabled) return;
    const start = this._phaseSpan.get(label);
    if (start == null) return;
    this._phaseSpan.delete(label);
    const ms = performance.now() - start;
    this._trackBucket(this._phaseStats, label, ms);
    this._frameBreakdown.set(label, (this._frameBreakdown.get(label) ?? 0) + ms);
  }

  /** Süreyi aynı frame içinde span tutmadan direk ekle. */
  addSample(label: string, ms: number): void {
    if (!this._enabled) return;
    this._trackBucket(this._phaseStats, label, ms);
    this._frameBreakdown.set(label, (this._frameBreakdown.get(label) ?? 0) + ms);
  }

  /** Bir entity adımı ölç — label = constructor adı veya benzeri. */
  addEntitySample(label: string, ms: number): void {
    if (!this._enabled) return;
    this._trackBucket(this._entityStats, label, ms);
  }

  /** Sayı ölçütü (pass count, entity count, vb.) — min/max/last izler. */
  setGauge(label: string, value: number): void {
    if (!this._enabled) return;
    let g = this._gauges.get(label);
    if (!g) {
      g = { last: value, min: value, max: value };
      this._gauges.set(label, g);
    } else {
      g.last = value;
      if (value < g.min) g.min = value;
      if (value > g.max) g.max = value;
    }
  }

  private _trackBucket(
    map: Map<string, BucketStats>,
    label: string,
    ms: number,
  ): void {
    let b = map.get(label);
    if (!b) {
      b = { count: 0, totalMs: 0, maxMs: 0, maxFrame: 0 };
      map.set(label, b);
    }
    b.count++;
    b.totalMs += ms;
    if (ms > b.maxMs) {
      b.maxMs = ms;
      b.maxFrame = this._frameIndex;
    }
  }

  private _logSlowFrame(): void {
    const rows: { label: string; ms: number }[] = [];
    this._frameBreakdown.forEach((ms, label) => {
      rows.push({ label, ms });
    });
    rows.sort((a, b) => b.ms - a.ms);
    const top = rows
      .slice(0, 6)
      .map((r) => r.label + "=" + r.ms.toFixed(1))
      .join(" ");
    // eslint-disable-next-line no-console
    console.warn(
      "[frameprof] SLOW frame#" +
        this._frameIndex +
        " total=" +
        this._frameTotalMs.toFixed(1) +
        "ms  " +
        top,
    );
  }

  private _flush(now: number): void {
    const elapsed = now - this._lastFlushMs;
    const frames = this._framesSinceFlush;
    const fps = frames > 0 ? (frames / elapsed) * 1000 : 0;
    const avg = frames > 0 ? this._totalSinceFlushMs / frames : 0;
    const max = this._maxFrameSinceFlushMs;

    const phaseRows = this._snapshotTopRows(this._phaseStats, 12);
    const entityRows = this._snapshotTopRows(this._entityStats, 8);
    const gaugeRows = Array.from(this._gauges.entries()).map(([label, g]) => ({
      label,
      last: g.last,
      min: g.min,
      max: g.max,
    }));

    // eslint-disable-next-line no-console
    console.groupCollapsed(
      "[frameprof] fps=" +
        fps.toFixed(1) +
        " avg=" +
        avg.toFixed(2) +
        "ms max=" +
        max.toFixed(1) +
        "ms frames=" +
        frames,
    );
    // eslint-disable-next-line no-console
    console.table(phaseRows);
    if (entityRows.length > 0) {
      // eslint-disable-next-line no-console
      console.log("entities:");
      // eslint-disable-next-line no-console
      console.table(entityRows);
    }
    if (gaugeRows.length > 0) {
      // eslint-disable-next-line no-console
      console.log("gauges:");
      // eslint-disable-next-line no-console
      console.table(gaugeRows);
    }
    // eslint-disable-next-line no-console
    console.groupEnd();

    this._phaseStats.clear();
    this._entityStats.clear();
    for (const g of this._gauges.values()) {
      g.min = g.last;
      g.max = g.last;
    }
    this._framesSinceFlush = 0;
    this._totalSinceFlushMs = 0;
    this._maxFrameSinceFlushMs = 0;
    this._lastFlushMs = now;
  }

  private _snapshotTopRows(
    map: Map<string, BucketStats>,
    topN: number,
  ): Array<{ label: string; avgMs: string; maxMs: string; count: number }> {
    const rows = Array.from(map.entries()).map(([label, b]) => ({
      label,
      avgMs: (b.totalMs / Math.max(1, b.count)).toFixed(2),
      maxMs: b.maxMs.toFixed(2),
      count: b.count,
      _sortBy: b.totalMs,
    }));
    rows.sort((a, b) => b._sortBy - a._sortBy);
    return rows.slice(0, topN).map(({ _sortBy: _s, ...rest }) => rest);
  }
}

/** Engine'in kullandığı singleton. Devre dışı başlar. */
export const frameProfiler = new FrameProfiler();

declare global {
  interface Window {
    __frameProfiler?: FrameProfiler;
  }
}

if (typeof window !== "undefined") {
  window.__frameProfiler = frameProfiler;
}
